import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import config from "./config";
import Home from "./Home";
import { getAppearancePreference } from "./plansAppearance";
import "./Plans.css";

export const reportOffers = [
  { key: "credit1", name: "1 credit", quantity: 1 },
  { key: "credit5", name: "5 credits", quantity: 5 },
  { key: "credit10", name: "10 credits", quantity: 10 },
  { key: "monthly10", name: "10 reports monthly", quantity: 10, monthly: true },
];

// Stripe minor units differ from ISO currency precision for ISK, UGX, and MGA.
// https://docs.stripe.com/currencies#zero-decimal
export function formatStripePrice(amount, currency) {
  const zeroDecimal = new Set(["bif", "clp", "djf", "gnf", "jpy", "kmf", "krw", "mga", "pyg", "rwf", "vnd", "vuv", "xaf", "xof", "xpf"]);
  return new Intl.NumberFormat(undefined, { style: "currency", currency })
    .format(amount / (zeroDecimal.has(currency.toLowerCase()) ? 1 : 100));
}

let pendingCatalog;
function loadCatalog() {
  if (!pendingCatalog) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    // Share the request through Strict Mode's effect replay; unmount only unsubscribes the view.
    pendingCatalog = fetch(`${config.API_URL.replace(/\/+$/, "")}/api/billing/credits/offers`, { signal: controller.signal })
      .then(response => { if (!response.ok) throw new Error("Catalog request failed"); return response.json(); })
      .finally(() => { clearTimeout(timeout); pendingCatalog = null; });
  }
  return pendingCatalog;
}

export default function Plans({ userEmail, isLoggedIn, onLogin, onForgotPassword, onCreateAccount }) {
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  useEffect(() => {
    let subscribed = true;
    setLoading(true);
    setError("");
    loadCatalog().then(result => {
      if (subscribed) setCatalog(result);
    }).catch(() => {
      if (subscribed) setError("Prices could not be loaded. Check your connection and try again.");
    }).finally(() => { if (subscribed) setLoading(false); });
    return () => { subscribed = false; };
  }, [attempt]);
  const temporaryFailure = catalog?.offers?.some(offer => offer.unavailableReason === "temporarily_unavailable");
  const appearance = getAppearancePreference();
  const signedInEmail = userEmail?.trim();
  return <main className={`report-plans report-plans--${appearance}`}>
    <div className="report-plans-inner">
      <header className="report-plans-header">
        <div>
          <p className="report-plans-eyebrow">SIGNALTUNER REPORTS</p>
          <h1>Connectivity Health Report credits and plans</h1>
          <p className="report-plans-intro">Choose credits as you need them, or a monthly allowance of 10 reports.</p>
        </div>
        {catalog?.testMode === true && <span className="plans-test-label">TEST MODE • NO PAYMENT</span>}
      </header>
      {error && <p role="alert" className="report-plans-alert">{error}</p>}
      {temporaryFailure && !error && <p role="alert" className="report-plans-alert">Some prices are temporarily unavailable. Please try again.</p>}
      {(error || temporaryFailure) && <button className="report-plans-retry" type="button" disabled={loading}
        onClick={() => setAttempt(value => value + 1)}>{loading ? "Loading prices..." : "Retry prices"}</button>}
      <div className="report-plan-grid">
        {reportOffers.map(offer => {
          const price = catalog?.offers?.find(item => item.key === offer.key);
          const formatted = price?.available ? formatStripePrice(price.amount, price.currency) : null;
          const badge = offer.key === "credit10" ? "SAVE $10" : offer.monthly ? "BEST VALUE" : null;
          return <article className={`report-plan-card report-plan-card--${offer.key}`} key={offer.key}>
            <div className="report-plan-card-top">
              <span className="report-plan-logo"><img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="SignalTuner" /></span>
              {badge && <span className="report-plan-badge">{badge}</span>}
            </div>
            <h2>{offer.name}</h2>
            <p className="report-plan-billing-type">{offer.monthly ? "Recurring purchase" : "One-time purchase"}</p>
            <p className="report-plan-price">{formatted || (price?.unavailableReason === "temporarily_unavailable" ? "Temporarily unavailable" : catalog ? "Not available yet" : error ? "Unavailable" : "Loading price...")}{formatted && offer.monthly && <span> / month</span>}</p>
            <p className="report-plan-description">{offer.monthly
              ? "10 reports per paid billing period. Report allowance replenished at renewal."
              : `${offer.quantity} ${offer.quantity === 1 ? "credit" : "credits"} added to your account. One credit per report.`}</p>
            <p className="report-plan-benefit">{offer.monthly
              ? "Monthly report allowance used before purchased credits"
              : "Credits do not expire"}</p>
            {price?.available ? <Link className="report-plan-button" to={`/credits/buy?offer=${offer.key}`}
              onClick={event => {
                if (!isLoggedIn) {
                  event.preventDefault();
                  setSelectedOffer(offer.key);
                }
              }}>
              {offer.monthly ? "Choose monthly plan" : `Buy ${offer.name}`}</Link>
              : <button disabled className="report-plan-button">{catalog && price?.unavailableReason !== "temporarily_unavailable" ? "Coming soon" : "Checkout unavailable"}</button>}
          </article>;
        })}
      </div>
      <div className="report-plans-account-notice">
        <p>{signedInEmail ? `Purchases credits and plans will be applied to ${signedInEmail}` : "Sign in to see which account receives your purchase."}</p>
        <p>Credits do not expire. Monthly plan allowances are replenished at time of renewal.</p>
      </div>
    </div>
    {selectedOffer && !isLoggedIn && <div className="plans-login-overlay" onMouseDown={event => {
      if (event.target === event.currentTarget) setSelectedOffer(null);
    }}>
      <div className="plans-login-dialog" role="dialog" aria-modal="true" aria-label="Sign in to continue to checkout"
        onKeyDown={event => { if (event.key === "Escape") setSelectedOffer(null); }}>
        <Home loginOnly loggedIn={false} showLoginForm
          loginClick={() => onLogin?.(selectedOffer)}
          showForgotPasswordClick={onForgotPassword}
          showUserCreationClick={onCreateAccount}
          hideHomePageFormsClick={() => setSelectedOffer(null)} />
      </div>
    </div>}
  </main>;
}
